import { Flex, Input, Select, Text, Tooltip } from '@byecode/ui'
import type { ArgumentsType, StateSchema } from '@lighthouse/core'
import { PAGE_PARAMETER_TYPE_OPTIONS, PopoverDropdownWithClose } from '@lighthouse/shared'
import { stopPropagation } from '@lighthouse/tools'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useLatest } from 'react-use'

import { StateInput } from './StateInput'

interface ParameterFormDropdownProps {
    data: StateSchema
    onUpdateItem?: (data: StateSchema) => void
    enableDefaultValue?: boolean
    existingNames: string[]
}

export const ParameterFormDropdown = ({ data, existingNames, enableDefaultValue, onUpdateItem }: ParameterFormDropdownProps) => {
    const [draftParameter, setDraftParameter] = useState<StateSchema>({ ...data })

    const latest = useLatest({ data, draftParameter, onUpdateItem })
    const hasRepeatError = useMemo(() => existingNames.includes(draftParameter.name), [draftParameter.name, existingNames])

    useEffect(() => {
        const ref = latest

        return () => {
            if (!ref.current.draftParameter.name || ref.current.data.name === ref.current.draftParameter.name) {
                return
            }
            ref.current.onUpdateItem?.({ ...ref.current.draftParameter })
        }
    }, [latest])

    const inputRef = useRef<HTMLInputElement | null>(null)

    return (
        <PopoverDropdownWithClose title={draftParameter.name} onClick={stopPropagation}>
            <Flex direction="column" p={8}>
                <Flex justifyContent="space-between" alignItems="center" styles={{ root: { height: 40 } }}>
                    <Text size={14}>参数名</Text>
                    <Tooltip
                        title="标题重复"
                        open={hasRepeatError}
                        placement="top-start"
                        styles={{ tooltip: { background: 'var(--color-red-500)' } }}
                    >
                        <Input
                            ref={inputRef}
                            styles={{
                                wrapper: {
                                    width: 180,
                                    '&:focus-within, &:focus': {
                                        borderColor: hasRepeatError ? 'var(--color-red-500)' : undefined
                                    }
                                }
                            }}
                            value={draftParameter.name}
                            onChange={e => {
                                const { value } = e.target
                                setDraftParameter(s => {
                                    return { ...s, name: value }
                                })
                            }}
                            onBlur={() => {
                                if (!draftParameter.name) {
                                    setDraftParameter(data)
                                    return
                                }

                                onUpdateItem?.({ ...draftParameter })
                            }}
                            onKeyDown={e => {
                                if (e.key === 'Enter') {
                                    inputRef.current?.blur()
                                    // if (!draftParameter.name) {
                                    //     setDraftParameter(data)
                                    //     return
                                    // }
                                    // onUpdateItem?.({ ...draftParameter })
                                }
                            }}
                        />
                    </Tooltip>
                </Flex>
                <Flex justifyContent="space-between" alignItems="center" styles={{ root: { height: 40 } }}>
                    <Text size={14}>类型</Text>
                    <Select
                        value={draftParameter.innerType}
                        onChange={v => {
                            setDraftParameter(s => {
                                return { ...s, innerType: v as ArgumentsType }
                            })
                            onUpdateItem?.({ ...draftParameter, innerType: v as ArgumentsType })
                        }}
                        options={PAGE_PARAMETER_TYPE_OPTIONS}
                        style={{ width: 180 }}
                    />
                </Flex>
                {enableDefaultValue && (
                    <Flex justifyContent="space-between" styles={{ root: { padding: '4px 0' } }}>
                        <Text mt={8} size={14}>
                            默认值
                        </Text>
                        <StateInput
                            innerType={draftParameter.innerType}
                            value={draftParameter.defaultValue}
                            onChange={v => {
                                setDraftParameter(s => {
                                    return { ...s, defaultValue: v }
                                })
                                onUpdateItem?.({ ...draftParameter, defaultValue: v })
                            }}
                        />
                    </Flex>
                )}
            </Flex>
        </PopoverDropdownWithClose>
    )
}
